import React, { useState, useEffect } from "react";
import DynamicCRUD from "../common/DynaminCrud";
import { getBooksConfig } from "./bookconfig";
import { useTimeZone } from "../../contexts/TimeZoneContext";
import PermissionDenied from "../../utils/permission_denied";
import { AuthHelper } from "../../utils/authHelper";
import DataApi from "../../api/dataApi";

const Books = ({ permissions, ...props }) => {
  const { timeZone } = useTimeZone();

  const [externalData, setExternalData] = useState({ 
    authors: [], 
    classifications: [], // Ensure this key matches what bookconfig expects
    publishers: [], 
    shelf: [], 
    branches: [] 
  });
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const isSuperAdmin = AuthHelper.isSuperAdmin?.();

  const fetchExternalData = async () => {
    try {
      setLoading(true);
      const [authorsRes, categoriesRes, publishersRes, ShelfResp, GroupedShelfResp, branchesRes] = await Promise.all([
        new DataApi("author").fetchAll(),
        new DataApi("classification").fetchAll(),
        new DataApi("publisher").fetchAll(),
        new DataApi("shelf").fetchAll(),
        new DataApi("shelf/grouped").fetchAll(),
        new DataApi("branches").fetchAll().catch(() => ({ data: [] }))
      ]);

      const authors = authorsRes?.data?.data || authorsRes?.data || [];
      const categories = categoriesRes?.data?.data || categoriesRes?.data || [];
      const publishers = publishersRes?.data?.data || publishersRes?.data || [];
      const shelf = ShelfResp?.data?.data || ShelfResp?.data || [];
      const groupedShelves = (GroupedShelfResp?.data || []).map(g => ({
        shelf_name: `${g.floor} - ${g.rack}`,
        sub_shelves: g.shelves || []
      }));
      const branches = branchesRes?.data?.data || branchesRes?.data || [];

      setExternalData({
        authors: Array.isArray(authors) ? authors : [],
        classifications: Array.isArray(categories) ? categories : [], // Maps backend categories to classifications
        publishers: Array.isArray(publishers) ? publishers : [],
        shelf: Array.isArray(shelf) ? shelf : [],
        groupedShelves: Array.isArray(groupedShelves) ? groupedShelves : [],
        branches: Array.isArray(branches) ? branches : [],
      });
    } catch (error) {
      console.error("Error fetching external data:", error);
      setExternalData({ authors: [], classifications: [], publishers: [], shelf: [], groupedShelves: [] });
    } finally {
      setLoading(false);
    }
  };

  function getCompanyIdFromToken() {
    const token = sessionStorage.getItem("token");
    if (!token) return null;

    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.companyid || payload.companyid || null;
  }
    
  const fetchCompany = async () => {
    try {
      const companyid = getCompanyIdFromToken();

      if (!companyid) return;

      const companyApi = new DataApi("company");
      const response = await companyApi.fetchById(companyid);

      if (response.data) {
        setCompany(response.data);
      }
    } catch (error) {
      console.error("Error fetching company by ID:", error);
    }
  };

  useEffect(() => {
    fetchExternalData();
    fetchCompany();
  }, []);

  if (!isSuperAdmin && !permissions?.allowView) {
    return <PermissionDenied />;
  }

  const finalConfig = getBooksConfig(
    { ...externalData, company },
    timeZone,
    {
      canCreate: permissions?.allowCreate,
      canEdit: permissions?.allowEdit,
      canDelete: permissions?.allowDelete
    }
  );

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <DynamicCRUD
      {...finalConfig}
      icon="fa-solid fa-book"
      permissions={permissions}
      authors={externalData.authors}
      classifications={externalData.classifications}
      publishers={externalData.publishers}
      shelf={externalData.shelf}
    />
  );
};

export default Books;